import { useState } from 'react';
import DocumentOverviewComponent from '~/components/ui/document/DocumentOverviewComponent';

import { DocumentFieldAPIModel } from '~/api/def/endpoints/document/documentFieldEndpoint';

import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import { Scaled } from 'react-pdf-highlighter-extended';
import { utils, write } from 'xlsx';
import { DocCompareChunkFinderChunk } from '~/api/def/endpoints/docCompare/docCompareChunkFinderEndpoint';
import { Spinner } from '~/components/ui/common/Spinner';
import { FieldList } from '~/components/ui/document/dialogs/documentOverviewDialog/FieldList';
import { useDocumentOverviewApi } from '~/components/ui/document/dialogs/documentOverviewDialog/useDocumentOverviewApi';
import { DocumentChatAi } from '~/components/ui/documentChatAi/DocumentChatAi';
import { LinkedDocumentsTable } from '~/components/ui/tables/ag/linkedDocuments/LinkedDocumentsTable';
import { CommentedHighlight } from '../vendor/pdf/pdf_container/types';
import DocumentCapTableContainer from './DocumentCapTableContainer';

export type DocumentOverviewContainerProps = {
  documentId?: string;
  source?: string;
  portfolioCompanyId?: string;
};

export const DocumentOverviewContainer = ({
  documentId,
  source = 'fundDocuments',
  portfolioCompanyId,
}: DocumentOverviewContainerProps) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [showChatWithDocument, setShowChatWithDocument] =
    useState<boolean>(false);

  const {
    documentFields,
    documentUrl,
    documentMeta,
    loadingDocumentMeta,
    createPcValuationEvent,
  } = useDocumentOverviewApi({ documentId });

  const [chatSelectedHighlight, setChatSelectedHighlight] =
    useState<CommentedHighlight>();

  const transformChunkToHighlight = (
    chunk: DocCompareChunkFinderChunk,
  ): CommentedHighlight => {
    const scaledPosition: Scaled = {
      x1: chunk.bbox_x1,
      y1: chunk.bbox_y1,
      x2: chunk.bbox_x2,
      y2: chunk.bbox_y2,
      width: chunk.bbox_x2 - chunk.bbox_x1,
      height: chunk.bbox_y2 - chunk.bbox_y1,
      pageNumber: chunk.bbox_page_start,
    };

    return {
      id: chunk.id,
      type: 'text',
      content: {
        text: chunk.text,
      },
      position: {
        boundingRect: scaledPosition,
        rects: [scaledPosition],
        usePdfCoordinates: true,
      },
    };
  };

  return (
    <div
      style={{
        position: 'relative',
        height: 'calc(100%)',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowY: 'hidden',
      }}
    >
      {loadingDocumentMeta || !documentMeta ? (
        <div className="flex align-items-center gap-2">
          <Spinner size={16} />
          <span>Loading document meta</span>
        </div>
      ) : (
        <>
          <div
            style={{ flexGrow: 0, display: 'flex' }}
            className="doc-overview grid grid-nogutter"
          >
            <div className="inner-container col col-3">
              <p className="label">Document Id</p>
              <p className="value">{documentMeta?.id}</p>
            </div>
            <div className="inner-container col col-3">
              <p className="label">Agreement type</p>
              <p className="value">{documentMeta?.type}</p>
            </div>
            <div className="inner-container col col-3">
              <p className="label">Counter Party</p>
              <p className="value">{documentMeta?.investorName}</p>
            </div>
            <div className="inner-container col col-3">
              <p className="label">Admission Date</p>
              <p className="value">
                {documentMeta?.admissionDate
                  ? format(new Date(documentMeta.admissionDate), 'yyyy-MM-dd')
                  : ''}
              </p>
            </div>
          </div>

          <div
            style={{
              position: 'relative',
              borderTop: '1px solid var(--bq-color-gray-border)',
              paddingTop: '12px',
              flexGrow: 1,
              overflowY: 'hidden',
            }}
          >
            <div
              className=""
              style={{
                position: 'absolute',
                right: '10px',
                top: '10px',
                zIndex: 10,
              }}
            >
              {source === 'portfolioDocuments' &&
                portfolioCompanyId &&
                selectedTab === 0 && (
                  <Button
                    label="Mark as Valuation event"
                    size="small"
                    icon="pi pi-calendar"
                    className="mr-4"
                    onClick={() => {
                      createPcValuationEvent(portfolioCompanyId);
                    }}
                  />
                )}
              <a
                href={documentUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="p-button p-button-secondary p-component p-button-sm mr-4"
                style={{
                  textDecoration: 'none',
                  pointerEvents: documentUrl ? 'auto' : 'none',
                  opacity: documentUrl ? 1 : 0.6,
                  color: 'white',
                  backgroundColor: '#007bff',
                  border: '1px solid #007bff',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = '#0168D9')
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = '#007bff')
                }
              >
                <span
                  className="pi pi-download"
                  style={{ marginRight: '0.5rem' }}
                ></span>
                <span className="p-button-label">Download document</span>
              </a>
              <Button
                label="Download fields"
                size="small"
                icon="pi pi-download"
                className="mr-4"
                onClick={() => {
                  if (documentFields) {
                    downloadExcel(documentFields);
                  }
                }}
              />
              <Button
                label="Chat with Your Document"
                size="small"
                icon={
                  showChatWithDocument
                    ? 'pi pi-chevron-right'
                    : 'pi pi-chevron-left'
                }
                className=""
                onClick={() => {
                  setShowChatWithDocument((prev) => !prev);
                }}
              />
            </div>
            <div className="flex flex-row w-full h-full">
              <TabView
                pt={{
                  panelContainer: {
                    style: {
                      height: '100%',
                      overflow: 'hidden',
                      marginLeft: '8px',
                      paddingLeft: '0px',
                      borderLeft: '1px solid var(--bq-color-gray-border)',
                    },
                  },
                }}
                activeIndex={selectedTab}
                onTabChange={(e) => {
                  setSelectedTab(e.index);
                }}
                style={{
                  height: '100%',
                  overflow: 'hidden',
                  marginLeft: '0.5rem',
                  transition: 'width 0.3s',
                  width: '100%',
                }}
              >
                <TabPanel header="Overview" style={{ height: '100%' }}>
                  {documentId && (
                    <DocumentOverviewComponent
                      overrideActiveHighlight={chatSelectedHighlight}
                      documentId={documentId}
                      source={source}
                    />
                  )}
                </TabPanel>
                {documentId && portfolioCompanyId && (
                  <TabPanel header="Cap Table" style={{ height: '100%' }}>
                    <DocumentCapTableContainer
                      documentId={documentId}
                      portfolioCompanyId={portfolioCompanyId}
                    />
                  </TabPanel>
                )}
                <TabPanel header="Linked Documents" style={{ height: '100%' }}>
                  <LinkedDocumentsTable documentId={documentId} />
                </TabPanel>
                <TabPanel
                  header="Field List"
                  style={{
                    height: '100%',
                  }}
                >
                  <FieldList documentFields={documentFields} />
                </TabPanel>
              </TabView>
              {documentId && (
                <DocumentChatAi
                  isOpen={showChatWithDocument}
                  documentId={documentId}
                  onClickCitation={(activeChunk) => {
                    const transformedHighlight =
                      transformChunkToHighlight(activeChunk);

                    setChatSelectedHighlight(transformedHighlight);
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function downloadExcel(data: DocumentFieldAPIModel[]) {
  // Prepare the data for the worksheet
  const worksheetData = data.map((item) => ({
    // id: item.id,
    name: item.name,
    value: item.value,
    // ... other fields as needed
  }));

  // Create the workbook and worksheet
  const workbook = utils.book_new();
  const worksheet = utils.json_to_sheet(worksheetData);
  utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // Generate the Excel file
  const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });

  // Create a Blob and URL object
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  // Create a data URL and trigger the download
  // const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${btoa(excelBuffer)}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = 'my_excel_file.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
